import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/motion";
import Breadcrumbs from "@/components/Breadcrumbs";
import Seo from "@/components/Seo";
import { MILLET_PAGE_LIST } from "@/data/milletContent";
import { breadcrumbSchema } from "@/lib/seo";

const guide = MILLET_PAGE_LIST.find((page) => page.slug === "millet-export-guide");
const hubs = MILLET_PAGE_LIST.filter((page) => ["pillar", "product", "commercial", "application", "market", "region"].includes(page.type));

export default function Resources() {
  const crumbs = [{ name: "Home", path: "/" }, { name: "Resources", path: "/resources" }];
  return (
    <div>
      <Seo
        title="Millet Export Resources | Buyer Guides and Market Information"
        description="Practical millet export resources for international buyers: Indian sourcing, pearl millet, green millet, bulk supply, food and feed applications, quality and market planning."
        path="/resources"
        jsonLd={[breadcrumbSchema(crumbs)]}
      />
      <section className="pt-36 md:pt-44 pb-16 bg-brand-green-dark">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Breadcrumbs items={crumbs} className="mb-8" /></Reveal>
          <Reveal><Overline className="text-brand-gold-light mb-5">Buyer resources</Overline></Reveal>
          <Reveal delay={0.05}><h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium text-brand-cream leading-none max-w-4xl">Useful information for millet importers and trade buyers.</h1></Reveal>
          <Reveal delay={0.1}><p className="text-lg text-brand-cream/75 leading-relaxed max-w-2xl mt-7">Explore practical guidance about Indian millet sourcing, product applications, quality, bulk supply and destination planning.</p></Reveal>
        </div>
      </section>
      <main className="bg-brand-cream py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <Reveal>
            <article className="bg-brand-green text-brand-cream p-8 md:p-12 rounded-sm">
              <Overline className="text-brand-gold-light">Featured guide</Overline>
              <h2 className="font-display text-4xl md:text-5xl mt-4">{guide.h1}</h2>
              <p className="text-brand-cream/75 leading-relaxed max-w-2xl mt-5">{guide.description}</p>
              <Link to={guide.path} className="inline-flex items-center gap-2 text-brand-gold mt-7 font-semibold">Read the buyer guide <ArrowRight size={16} /></Link>
            </article>
          </Reveal>
          <div className="mt-16">
            <Reveal><Overline className="text-brand-gold-dark mb-4">Explore the cluster</Overline></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
              {hubs.filter((page) => page.slug !== "millet-export-guide").map((page) => (
                <Reveal key={page.slug}>
                  <Link to={page.path} className="block bg-white border border-brand-line p-7 h-full hover:border-brand-gold transition-colors">
                    <span className="text-xs uppercase tracking-[0.18em] text-brand-gold-dark font-semibold">{page.eyebrow}</span>
                    <h2 className="font-display text-3xl text-brand-green mt-3">{page.h1}</h2>
                    <p className="text-sm text-brand-muted leading-relaxed mt-3">{page.description}</p>
                    <span className="inline-flex items-center gap-2 text-sm text-brand-green font-semibold mt-6">Explore <ArrowRight size={15} /></span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
